/**
 * @file Album Folder Purchase - Select Identity Tags.
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import get from 'lodash.get';
import uniqBy from 'lodash.uniqby';

import IdentityAvatarContainer from '../../../containers/global/identity/IdentityAvatarContainer';
import { AVATAR_SIZE_52 } from '../../../constants/avatar-sizes';
import EditIcon from '../../icons/EditIcon';
import IdentityNameContainer from '../../../containers/global/identity/IdentityNameContainer';
import Badge from '../../global/Badge';
import AlbumFolderPurchaseSelectableTag from './AlbumFolderPurchaseSelectableTag';
import {
  UPDATE_ACCOUNT_IDENTITY_MODAL,
  UNIFORM_TAG_FORM_MODAL,
  GRADE_TAG_FORM_MODAL,
} from '../../../constants/modals';
import {
  getEligibleUniformTagsForIdentity,
  getIdentityGrade,
  getSortedAlbumFolderGradeTags,
  sortUniformTagsByLabel,
} from '../../../util/album-folder-tags';
import { ALBUM_FOLDER_TAG_TYPE } from '../../../constants/album-folder-tags';
import Button from '../../global/Button';

class AlbumFolderPurchaseIdentityTagSelection extends Component {
  static propTypes = {
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.oneOf(Object.keys(ALBUM_FOLDER_TAG_TYPE)),
        uniform_enabled: PropTypes.bool,
        uniform_numbered: PropTypes.bool,
        levels: PropTypes.shape({
          records: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          ),
        }),
      }),
    ),
    identity: PropTypes.shape({
      id: PropTypes.string,
      purchased_album_folders: PropTypes.shape({
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
          }),
        ),
      }),
    }),
    selectedTags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        type: PropTypes.oneOf(Object.keys(ALBUM_FOLDER_TAG_TYPE)),
        uniform_enabled: PropTypes.bool,
        uniform_numbered: PropTypes.bool,
        levels: PropTypes.shape({
          records: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          ),
        }),
        identityMetadata: PropTypes.shape({
          jerseyNumber: PropTypes.string,
          levelId: PropTypes.string,
        }),
      }),
    ),
    selectTag: PropTypes.func.isRequired,
    deselectTag: PropTypes.func.isRequired,
    openModal: PropTypes.func,
    uploadedPhoto: PropTypes.object,
    defaultExpanded: PropTypes.bool,
  };

  state = {
    expanded: this.props.defaultExpanded || false,
  };

  onEditIdentity = event => {
    event.preventDefault();
    event.stopPropagation();

    const { identity, uploadedPhoto, openModal } = this.props;

    openModal(UPDATE_ACCOUNT_IDENTITY_MODAL, {
      identity,
      forceSelfieUpdate: !identity.current_photo && !uploadedPhoto,
      outdatedSelfie: identity.has_outdated_selfie && !uploadedPhoto,
    });
  };

  onEditGrade = event => {
    const { openModal, identity, tags } = this.props;

    event.preventDefault();
    event.stopPropagation();

    const gradeTags = getSortedAlbumFolderGradeTags(tags);

    openModal(GRADE_TAG_FORM_MODAL, {
      identity,
      gradeTags,
    });
  };

  getTagMetadata = tag => {
    const { selectedTags, identity } = this.props;

    const identityTags = identity?.album_folder_tags?.records ?? [];

    const subscriptionTag = identityTags.find(
      ({ album_folder_tag: { id } }) => id === tag.id,
    );

    const selectedTag = selectedTags.find(({ id }) => tag.id === id);

    if (subscriptionTag && !selectedTag) {
      return {
        jerseyNumber: subscriptionTag?.jersey_number,
        levelId: subscriptionTag?.uniform_album_folder_tag_level?.id,
        teamId: subscriptionTag?.uniform_album_folder_tag_level_team?.id,
      };
    }

    if (!selectedTag) {
      return null;
    }

    return selectedTag.identityMetadata;
  };

  isTagSelected = tag => {
    const { selectedTags } = this.props;

    return selectedTags.some(({ id }) => tag.id === id);
  };

  toggleExpanded = event => {
    if (event) {
      event.preventDefault();
    }

    this.setState(({ expanded }) => ({ expanded: !expanded }));
  };

  toggleTag = tag => {
    const { selectTag, deselectTag, identity, openModal } = this.props;

    if (this.isTagSelected(tag)) {
      deselectTag(identity, tag);
      return;
    }

    const uniformEnabled = get(tag, 'uniform_enabled');
    const hasLevels = get(tag, 'levels.records.length', 0) > 0;

    if (uniformEnabled || hasLevels) {
      openModal(UNIFORM_TAG_FORM_MODAL, {
        identity,
        tag,
        onSuccess: () => this.setState(() => ({ expanded: false })),
      });
    } else {
      selectTag(identity, tag);
    }
  };

  render() {
    const { identity, tags, selectedTags, openModal } = this.props;
    const { expanded } = this.state;

    const uniformTags = sortUniformTagsByLabel(
      getEligibleUniformTagsForIdentity(identity.gender, tags),
    );
    const gradeTags = getSortedAlbumFolderGradeTags(tags);

    if (uniformTags.length === 0) {
      // If no tags can be assigned to this identity,
      // turn around and do nothing.
      return null;
    }

    const identityTags = get(identity, 'album_folder_tags.records', []);
    const identityGradeTag = getIdentityGrade(identity);

    const selectedAndIdentityTags = uniqBy(
      [
        ...identityTags.map(identityTag =>
          get(identityTag, 'album_folder_tag'),
        ),
        ...selectedTags,
      ],
      'id',
    );

    return (
      <Fragment>
        <a href="#" className="flex-list-item" onClick={this.toggleExpanded}>
          <IdentityAvatarContainer
            identity={identity}
            className="mr-2"
            onClick={this.onEditIdentity}
            size={AVATAR_SIZE_52}
          >
            <span className="btn-identity-edit">
              <EditIcon color="#0d84fe" />
            </span>
          </IdentityAvatarContainer>

          <div className="d-flex flex-column align-items-start">
            <IdentityNameContainer
              className="text-dark text-bold mr-1"
              identity={identity}
            />

            {gradeTags.length > 0 && (
              <Button
                className="mt-1"
                btnStyle="reset"
                onClick={this.onEditGrade}
              >
                <Badge badgeStyle="secondary">
                  <EditIcon size="10" className="mr-1" />
                  {identityGradeTag
                    ? `Grade: ${identityGradeTag.name}`
                    : 'Add Grade'}
                </Badge>
              </Button>
            )}

            {selectedAndIdentityTags.length > 0 && (
              <Badge className="mt-1" badgeSize="xs">
                {selectedAndIdentityTags.length}{' '}
                {pluralize('filter', selectedAndIdentityTags.length)} selected
              </Badge>
            )}
          </div>

          <button className="ml-auto text-sm text-primary">
            Filters
            {expanded ? (
              <span className="ml-1 caret-up" />
            ) : (
              <span className="ml-1 caret-down" />
            )}
          </button>
        </a>

        {expanded && (
          <div className="flex-list-item-children">
            {uniformTags.map(tag => (
              <AlbumFolderPurchaseSelectableTag
                tag={tag}
                key={tag.id}
                isSelected={this.isTagSelected(tag)}
                onTagSelected={this.toggleTag}
                metadata={this.getTagMetadata(tag)}
                onEditTag={() => {
                  const subscriptionTag = identityTags.find(
                    ({ album_folder_tag: { id } }) => id === tag.id,
                  );

                  openModal(UNIFORM_TAG_FORM_MODAL, {
                    identity,
                    tag,
                    subscriptionTag,
                    metadata: this.getTagMetadata(tag),
                  });
                }}
                subscriptionTag={identityTags.find(
                  ({ album_folder_tag: { id } }) => id === tag.id,
                )}
              />
            ))}
          </div>
        )}
      </Fragment>
    );
  }
}

export default AlbumFolderPurchaseIdentityTagSelection;
